export const MAX_HEADER_CHARS = 100_000;

export type HeaderField = { name: string; value: string };
export type AuthAssertion = {
	reporter: string;
	mechanism: string;
	result: string;
	detail: string;
};
export type ReceivedHop = {
	indexFromTop: number;
	raw: string;
	fromHost?: string;
	ip?: string;
	byHost?: string;
	date?: string;
	uncertain: string[];
};
export type EmailAnalysis = {
	ok: boolean;
	status: 'analyzed' | 'empty' | 'too-large' | 'error';
	message: string;
	fields: HeaderField[];
	from?: string;
	replyTo?: string;
	returnPath?: string;
	messageId?: string;
	date?: string;
	auth: AuthAssertion[];
	hops: ReceivedHop[];
	notes: string[];
	report: string;
};

function decodeMimeWord(token: string): string {
	const m = /^=\?([^?]+)\?([bBqQ])\?([^?]*)\?=$/.exec(token);
	if (!m) {
		return token;
	}
	const charset = m[1].toLowerCase();
	const encoding = m[2].toUpperCase();
	const data = m[3];
	try {
		if (encoding === 'B') {
			const bin = atob(data.replace(/\s+/g, ''));
			const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
			return new TextDecoder(charset === 'utf-8' || charset === 'utf8' ? 'utf-8' : 'utf-8').decode(bytes);
		}
		const q = data
			.replace(/_/g, ' ')
			.replace(/=([0-9A-Fa-f]{2})/g, (_, h: string) => String.fromCharCode(parseInt(h, 16)));
		return q;
	} catch {
		return token;
	}
}

export function decodeMimeWords(value: string): string {
	return value.replace(/=\?[^?]+\?[bBqQ]\?[^?]*\?=/g, (token) => decodeMimeWord(token));
}

/** Unfold RFC5322 folded header block; stop at blank line (body). */
export function unfoldHeaders(raw: string): HeaderField[] {
	const lines = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
	const fields: HeaderField[] = [];
	let current: HeaderField | null = null;

	for (const line of lines) {
		if (line === '') {
			break;
		}
		if (/^[ \t]/.test(line) && current) {
			current.value += ' ' + line.trim();
			continue;
		}
		const idx = line.indexOf(':');
		if (idx <= 0) {
			if (current) {
				current.value += ' ' + line.trim();
			}
			continue;
		}
		if (current) {
			fields.push(current);
		}
		current = {
			name: line.slice(0, idx).trim(),
			value: line.slice(idx + 1).trim(),
		};
	}
	if (current) {
		fields.push(current);
	}
	return fields;
}

function firstField(fields: HeaderField[], name: string): string | undefined {
	const lower = name.toLowerCase();
	const hit = fields.find((f) => f.name.toLowerCase() === lower);
	return hit ? decodeMimeWords(hit.value) : undefined;
}

function allFields(fields: HeaderField[], name: string): string[] {
	const lower = name.toLowerCase();
	return fields.filter((f) => f.name.toLowerCase() === lower).map((f) => decodeMimeWords(f.value));
}

function parseAuthResults(values: string[]): AuthAssertion[] {
	const out: AuthAssertion[] = [];
	for (const value of values) {
		const parts = value.split(';').map((p) => p.trim()).filter(Boolean);
		if (!parts.length) {
			continue;
		}
		const reporter = parts[0].split(/\s+/)[0] || 'unknown';
		for (let i = 1; i < parts.length; i += 1) {
			const part = parts[i];
			const m = /^(spf|dkim|dmarc)\s*=\s*([a-z0-9_-]+)\b(.*)$/i.exec(part);
			if (!m) {
				continue;
			}
			out.push({
				reporter,
				mechanism: m[1].toUpperCase(),
				result: m[2].toLowerCase(),
				detail: part,
			});
		}
	}
	return out;
}

function parseReceived(values: string[]): ReceivedHop[] {
	return values.map((raw, indexFromTop) => {
		const uncertain: string[] = [
			'Received hops are untrusted and may be forged or incomplete.',
		];
		const fromHost = /\bfrom\s+(\S+)/i.exec(raw)?.[1];
		const byHost = /\bby\s+(\S+)/i.exec(raw)?.[1];
		const ip =
			/\[((?:\d{1,3}\.){3}\d{1,3}|[0-9a-fA-F:]+)\]/.exec(raw)?.[1] ||
			undefined;
		const date = /;\s*(.+)$/.exec(raw)?.[1]?.trim();
		if (!date) {
			uncertain.push('Timestamp missing or not recognized.');
		} else if (Number.isNaN(Date.parse(date))) {
			uncertain.push('Timestamp could not be parsed.');
		}
		if (indexFromTop === values.length - 1) {
			uncertain.push('Earliest hop is not definitive proof of origin.');
		}
		return { indexFromTop, raw, fromHost, ip, byHost, date, uncertain };
	});
}

function extractEmailish(value: string | undefined): string | undefined {
	if (!value) {
		return undefined;
	}
	const m = /<([^>]+)>/.exec(value) || /([^\s<>]+@[^\s<>]+)/.exec(value);
	return m ? m[1] : value;
}

export function analyzeEmailHeaders(raw: string): EmailAnalysis {
	const trimmed = raw.trim();
	if (!trimmed) {
		return {
			ok: false,
			status: 'empty',
			message: 'Paste raw email headers to analyze.',
			fields: [],
			auth: [],
			hops: [],
			notes: [],
			report: '',
		};
	}
	if (trimmed.length > MAX_HEADER_CHARS) {
		return {
			ok: false,
			status: 'too-large',
			message: `Input exceeds the ${MAX_HEADER_CHARS.toLocaleString()} character limit.`,
			fields: [],
			auth: [],
			hops: [],
			notes: [],
			report: '',
		};
	}

	try {
		const fields = unfoldHeaders(trimmed);
		const from = firstField(fields, 'From');
		const replyTo = firstField(fields, 'Reply-To');
		const returnPath = firstField(fields, 'Return-Path');
		const messageId = firstField(fields, 'Message-ID');
		const date = firstField(fields, 'Date');
		const auth = parseAuthResults(allFields(fields, 'Authentication-Results'));
		const hops = parseReceived(allFields(fields, 'Received'));
		const notes: string[] = [
			'Authentication-Results in a paste can be forged; this is not independent SPF/DKIM/DMARC verification.',
		];

		const fromAddr = extractEmailish(from);
		const replyAddr = extractEmailish(replyTo);
		if (fromAddr && replyAddr && fromAddr.toLowerCase() !== replyAddr.toLowerCase()) {
			notes.push(`From (${fromAddr}) differs from Reply-To (${replyAddr}). Common in legitimate mail and phishing — investigate context.`);
		}
		for (const a of auth) {
			if (a.result === 'fail' || a.result === 'softfail') {
				notes.push(`${a.mechanism} reported as ${a.result} by ${a.reporter} (unverified paste).`);
			}
		}
		if (/<script|javascript:/i.test(trimmed)) {
			notes.push('Input contained HTML/script-looking fragments; values are shown as plain text only.');
		}

		const reportLines = [
			'KB Email Header Analyzer — sanitized report',
			`Status: analyzed`,
			`From: ${from || '—'}`,
			`Reply-To: ${replyTo || '—'}`,
			`Return-Path: ${returnPath || '—'}`,
			`Message-ID: ${messageId || '—'}`,
			`Date: ${date || '—'}`,
			`Auth assertions: ${auth.length}`,
			...auth.map((a) => `  - ${a.reporter}: ${a.mechanism}=${a.result}`),
			`Received hops (top first): ${hops.length}`,
			...hops.map((h, i) => `  ${i + 1}. ${h.fromHost || '?'} → ${h.byHost || '?'} [${h.ip || 'no-ip'}] ; ${h.date || 'no-date'}`),
			'Notes:',
			...notes.map((n) => `  - ${n}`),
		];

		return {
			ok: true,
			status: 'analyzed',
			message: 'Headers analyzed locally. Findings are evidence to interpret, not proof of fraud.',
			fields,
			from,
			replyTo,
			returnPath,
			messageId,
			date,
			auth,
			hops,
			notes,
			report: reportLines.join('\n'),
		};
	} catch {
		return {
			ok: false,
			status: 'error',
			message: 'Could not parse these headers.',
			fields: [],
			auth: [],
			hops: [],
			notes: [],
			report: '',
		};
	}
}
